import { serviceOrigin } from '../lib/services';
// Direct content-api access for the FragmentTree view. Same JWT works
// across auth-api / cloud-api / content-api (shared JWKS, RS256). Pattern
// mirrors RbacAdmin's direct AUTH_API fetch — we route through the
// federation node that hosts content-api rather than going via the
// local cloud-api proxy.
//
// When Cloud ships `GET /api/prompt-fragments` (his ef40eb9-style
// federation aggregator), we can swap these calls to that single
// roundtrip and drop the cross-API hop. For Phase-2 MVP this direct
// access is the path-of-least-friction.

const contentApiOrigin = () => serviceOrigin('content');

// ── field_map shapes (#226) — mirrors the MCP tool's serializer ──
export interface FieldMapNode {
  id: string;
  type: 'agent' | 'block' | 'section' | 'concept_candidate' | 'tension' | string;
  properties?: Record<string, unknown> & {
    display_name?: string;
    session_name?: string;
    color?: string;
    title?: string;
    starts_at_block?: number;
    density?: { char_count: number; n_authors: number; n_blocks: number };
    potential?: {
      author_shares: Record<string, number>;
      diffusion: number;
      dominance_score: number;
      dominant_author_id: string;
    };
  };
}
export interface FieldMapEdge { from: string; to: string; type: string; weight?: number }
export interface FieldMapClaim {
  claim_id: string;
  agent: string;
  intent?: string;
  expires_at?: string;
  region?: { type: string; ref: string };
  /** Backend-resolved mapping into field_map space (#230): which heading-sections/
   *  blocks the claim covers. Optional — absent until Content ships the enrichment. */
  resolved?: { section_slugs?: string[]; block_indices?: number[] };
}
export interface FieldMapResponse {
  scope?: { post_id: number };
  nodes: FieldMapNode[];
  edges: FieldMapEdge[];
  active_claims?: FieldMapClaim[];
  metrics?: {
    tensions_count?: number;
    sections_by_density?: unknown[];
    sections_by_dominance?: unknown[];
  };
}
const AUTH_STORAGE_KEY = 'ark-auth';

function authHeader(): Record<string, string> {
  try {
    const raw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return {};
    const stored = JSON.parse(raw);
    return stored?.access_token ? { Authorization: `Bearer ${stored.access_token}` } : {};
  } catch {
    return {};
  }
}

async function get<T>(path: string): Promise<T> {
  const base = contentApiOrigin();
  if (!base) throw new Error('content_service_unavailable');
  const res = await fetch(`${base}${path}`, { headers: authHeader() });
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    throw new Error(txt || `content-api ${res.status}: ${path}`);
  }
  return res.json();
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const base = contentApiOrigin();
  if (!base) throw new Error('content_service_unavailable');
  const res = await fetch(`${base}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    throw new Error(txt || `content-api ${res.status}: ${path}`);
  }
  // Reply endpoint returns 201 with no body.
  const text = await res.text().catch(() => '');
  return (text ? JSON.parse(text) : undefined) as T;
}

async function patchJson<T>(path: string, body: unknown): Promise<T> {
  const base = contentApiOrigin();
  if (!base) throw new Error('content_service_unavailable');
  const res = await fetch(`${base}${path}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...authHeader() },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    throw new Error(txt || `content-api ${res.status}: ${path}`);
  }
  const text = await res.text().catch(() => '');
  return (text ? JSON.parse(text) : undefined) as T;
}

export interface ContentCollection {
  id: number;
  parent_id: number | null;
  name: string;
  slug: string;
  description: string;
  color: string | null;
  icon: string | null;
  position: number;
  post_count: number;
  children_count: number;
  children: ContentCollection[];
}

export interface ContentPostSummary {
  id: number;
  title: string;
  slug: string;
  doc_type: string;
  status: string;
  content_type: string;
  author_id: string | null;
  author_name: string | null;
  created_at: string;
  updated_at: string;
}

export interface ContentPostDetail extends ContentPostSummary {
  content: string;
  metadata_json: Record<string, unknown> | null;
  partner_id: string | null;
  tags: string[];
  language: string | null;
}

// ── Collab sections (Content IACP 0ae3d174) ──────────────────────────
// A section is a typed region of a collab post. `question` carries a
// reply-thread; each paragraph with section_role='reply' is one bubble.
export interface CollabSectionSummary {
  id: string;
  type: 'question' | 'task' | 'vote' | 'product' | 'annotation' | string;
  status: string;
  paragraph_count: number;
  role_start_index?: number;
  role_end_index?: number;
  char_start?: number;
  char_end?: number;
  // initiator / targets (comma-list) / primary_recipient / status …
  attrs?: Record<string, string>;
}

export interface CollabParagraph {
  index: number;
  text: string;
  author_id: string | null;   // 'agent:Cloud' | 'user:<uuid>' | null
  author_name: string | null;
  block_id?: string;
  section_role?: 'start' | 'reply' | string;
}

export interface CollabSectionDetail extends CollabSectionSummary {
  paragraphs: CollabParagraph[];
}

export interface CollabInboxPost {
  id: number;
  title: string;
  subtitle: string | null;
  updated_at: string;
  created_at: string;
  metadata_json: { notify_authors?: string[] } | null;
  // Distinct section types present in the body, sorted (Content commit
  // a06baa8) — drives the per-thread section badges without a per-post fetch.
  section_types?: string[] | null;
}

// Flat row from `GET /api/v1/collections/` — no `children` field, tree
// is assembled client-side. `collectionsTree()` returns the assembled
// version with each node's `children` populated recursively.
type FlatCollection = Omit<ContentCollection, 'children'>;

function assembleTree(flat: FlatCollection[]): ContentCollection[] {
  const byId = new Map<number, ContentCollection>();
  flat.forEach((c) => byId.set(c.id, { ...c, children: [] }));
  const roots: ContentCollection[] = [];
  byId.forEach((node) => {
    if (node.parent_id == null) {
      roots.push(node);
    } else {
      const parent = byId.get(node.parent_id);
      if (parent) parent.children.push(node);
      else roots.push(node); // orphan — surface at root level
    }
  });
  return roots;
}

export const contentApi = {
  // field_map graph data (#226 Phase 2b) — thin REST wrapper around the MCP-tool
  // service (Content builds it; 404 until deployed → panel shows a pending state).
  fieldMap: (postId: number, detail: 'compact' | 'metrics' = 'metrics') =>
    get<FieldMapResponse>(`/api/v1/posts/${postId}/field_map?detail=${detail}`),

  // Invite agents into an existing collab room (Alex 2026-07-03): adds them to
  // notify_authors + sends an IACP onboarding ping + skips offline agents.
  // Mirrors mcp posts_invite — POST /api/v1/posts/{id}/invite.
  invite: (postId: number, agentNames: string[], message?: string) =>
    postJson<{ invited?: string[]; offline_skipped?: string[]; already_subscribed?: string[] }>(
      `/api/v1/posts/${postId}/invite`,
      { agent_names: agentNames, ...(message ? { message } : {}) },
    ),

  // Remove agents from a collab room's notify_authors (Content IACP 1392e9ef,
  // 2026-07-22): atomic server-side read-modify-write (no last-write-wins race),
  // no IACP ping (pure list-op). `notify_authors_after` is the authoritative new
  // list → adopt it as participants state without a refetch. Mirrors POST
  // /api/v1/posts/{id}/uninvite.
  uninvite: (postId: number, agentNames: string[]) =>
    postJson<{ post_id: number; removed: string[]; not_subscribed: string[]; notify_authors_after: string[] }>(
      `/api/v1/posts/${postId}/uninvite`,
      { agent_names: agentNames },
    ),

  // Content-API has no `/tree` endpoint — it returns a FLAT list at
  // `GET /api/v1/collections/`. The MCP tool `collections_tree` is a
  // server-side convenience that does the assembly server-side; we
  // mirror that client-side here so we can stay on the public REST API
  // (the MCP tool isn't callable from a browser).
  collectionsTree: async (): Promise<ContentCollection[]> => {
    const flat = await get<FlatCollection[]>('/api/v1/collections/');
    return assembleTree(flat);
  },

  // Collection detail endpoint (GET /api/v1/collections/{slug}) returns
  // posts nested in the response — no flat per-collection-posts list
  // exists. We filter client-side to `doc_type=prompt_fragment` so the
  // Tree-View only shows fragments even when a collection legitimately
  // holds other doc types alongside.
  postsInCollectionBySlug: async (slug: string) => {
    const detail = await get<{ posts: (ContentPostSummary & { doc_type?: string })[] }>(
      `/api/v1/collections/${encodeURIComponent(slug)}`,
    );
    const posts = (detail.posts || []).filter((p) => p.doc_type === 'prompt_fragment');
    return { posts: posts as ContentPostSummary[], total: posts.length };
  },

  // Single post fetch for the body+metadata preview on the right panel.
  post: (postId: number) => get<ContentPostDetail>(`/api/v1/posts/${postId}`),

  // ── Collab-View (S1) ──────────────────────────────────────────────
  // List a post's sections, optionally filtered by type/status.
  sections: (postId: number, opts?: { type?: string; status?: string }) => {
    const q = new URLSearchParams();
    if (opts?.type) q.set('type', opts.type);
    if (opts?.status) q.set('status', opts.status);
    const qs = q.toString();
    return get<CollabSectionSummary[]>(
      `/api/v1/posts/${postId}/sections/${qs ? `?${qs}` : ''}`,
    );
  },
  // One section with its paragraphs (= the reply bubbles for a question).
  sectionDetail: (postId: number, sectionId: string) =>
    get<CollabSectionDetail>(`/api/v1/posts/${postId}/sections/${sectionId}`),
  // Human reply to a question/task/annotation section. `by` omitted →
  // server stamps the calling JWT's identity. 201, no body.
  // `targets` (single agent or comma-list) makes it a DIRECTED reply: only
  // those get the push and the reply-tag is stamped `direct_to` so the FE
  // can render a "→ an X" label (Alex 2026-06-20, Content V1 backend).
  reply: (postId: number, sectionId: string, text: string, targets?: string) =>
    postJson<void>(`/api/v1/posts/${postId}/sections/${sectionId}/reply`,
      targets ? { text, by: null, targets } : { text, by: null }),

  // ── Collab create (S2) — verified contract 2026-06-20, trailing slash
  // is REQUIRED on both (content-api 307-redirects without it). ──────────
  createCollabPost: (title: string, notifyAuthors: string[]) =>
    postJson<ContentPostDetail>('/api/v1/posts/', {
      title,
      doc_type: 'collab_text',
      collab_enabled: true,
      metadata_json: { notify_authors: notifyAuthors },
    }),
  createQuestion: (postId: number, text: string, attrs: Record<string, string>) =>
    postJson<CollabSectionSummary>(`/api/v1/posts/${postId}/sections/`, {
      type: 'question',
      attrs,
      initial_text: `<main>${text}</main>`,
    }),
  // Task section (S5) — attrs: assignee, status, priority, due (Content
  // contract 2026-06-20). Body = the task description (plain initial_text).
  createTask: (postId: number, description: string, attrs: Record<string, string>) =>
    postJson<CollabSectionSummary>(`/api/v1/posts/${postId}/sections/`, {
      type: 'task',
      attrs,
      initial_text: description,
    }),
  // Status lifecycle open→in_progress→done (tap on the status pill). Backend
  // validates the transition and returns the updated section.
  setSectionStatus: (postId: number, sectionId: string, status: string) =>
    patchJson<CollabSectionSummary>(`/api/v1/posts/${postId}/sections/${sectionId}`, { status }),
  // Word-level authorship (V2.3, P4) — per-paragraph `authorship_runs` with
  // Unicode-codepoint offsets, gapless, adjacent-same-author merged. Backend
  // supplies the deterministic `author_color` (HSL). Plain mode = no query.
  structuredAuthorship: (postId: number) =>
    get<{
      post_id: number;
      version: number;
      paragraphs: {
        index: number;
        author_id: string | null;
        text: string;
        authorship_runs: { start: number; end: number; author_id: string; author_name: string; author_color: string; commit_id?: string; source?: string }[] | null;
      }[];
    }>(`/api/v1/posts/${postId}/crdt/structured?include_authorship=1`),
  // Vote section (S6) — attrs: question, options (comma-list ≥2), voters
  // (comma-list | "open"), threshold (majority|all|<int>) (Content contract).
  createVote: (postId: number, attrs: Record<string, string>) =>
    postJson<CollabSectionSummary>(`/api/v1/posts/${postId}/sections/`, { type: 'vote', attrs }),
  // Cast a ballot — idempotent per voter, re-vote overwrites. Server tallies;
  // at threshold the status flips to tallied/tied and stamps attrs.winner.
  castVote: (postId: number, sectionId: string, choice: string) =>
    postJson<CollabSectionSummary>(`/api/v1/posts/${postId}/sections/${sectionId}/vote`, { choice }),
  // Product section (S7) — attrs: name, mode (draft|improve|review|final),
  // goal?, owner?, editors?, reviewers? (Content contract). Body = the
  // Markdown artifact; edited later via the CRDT editor (V1 iframe).
  createProduct: (postId: number, attrs: Record<string, string>, body: string) =>
    postJson<CollabSectionSummary>(`/api/v1/posts/${postId}/sections/`, {
      type: 'product',
      attrs,
      initial_text: body,
    }),

  // ── Group-chat overview (Content IACP 7c0e1963) — collab posts where the
  // given identity is a participant, newest-active first. Trailing slash. ──
  collabInbox: (userName: string, limit = 50) =>
    get<{ posts: CollabInboxPost[]; total: number }>(
      `/api/v1/posts/?collab_enabled=true&notify_author=${encodeURIComponent(userName)}&order_by=updated_at&limit=${limit}`,
    ),
};
